<script setup lang="ts">
import { useChangelogs } from '@/composeables/use-changelogs';
import { RouteName } from '@/router/models/route-name';
import router from '@/router/router';
import { ataService } from '@/services/ata/ata-service';
import { entraAuthService } from '@/services/auth/entra-auth-service.ts';
import { ataChangelogsService } from '@/services/changelogs/ata-changelogs-service';
import { notificationService } from '@/services/notification/notification-service';
import { projectKeys } from '@/utils/queryKeys';
import { useQueryClient } from '@tanstack/vue-query';
import { models, utils } from 'cesg-shared';
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute } from 'vue-router';
import AtaDetails from '../AtaDetails.vue';
import { DocumentUploadOverlay } from '../sub-components/document-upload-overlay';
import type {
  AtaChangelog,
  AtaDocumentPayload,
  AtaFormData,
  AtaSaveCallbacks,
} from '../types';
import AtaDetailsSkeleton from './AtaDetailsSkeleton.vue';
import { useAtaChangelogsQuery } from './useAtaChangelogsQuery';
import { useAtaDebitNormsQuery } from './useAtaDebitNormsQuery';
import { useAtaDetailsMutation } from './useAtaDetailsMutation';
import { useAtaDetailsQuery } from './useAtaDetailsQuery';
import { useAtaDocumentQuery } from './useAtaDocumentQuery';
import { useAtaProjectDetailsQuery } from './useAtaProjectDetailsQuery';

const route = useRoute();
const { t } = useI18n();
const { tChangelog } = useChangelogs('Ata');

const projectId = utils.type.asType<string>(route.params.id);
const ataId = ref(utils.type.asType<string>(route.params.ataId));

const currentUser: models.GetMeResponse | null = entraAuthService.me.value;

const isCreate = computed(() => ataId.value === '0');

function mapGetAtaResponseToFormData(
  response: models.GetAtaResponse,
): AtaFormData {
  return {
    projectId: response.projectId,
    id: response.id ?? '',
    title: response.title ?? null,
    status: response.status ?? null,
    serialNumber: response.serialNumber ?? null,
    description: response.description ?? null,
    shouldBeRegulated: response.shouldBeRegulated ?? null,
    leadsToTimeExtension: response.leadsToTimeExtension ?? null,
    pricingModel: response.pricingModel ?? null,
    shouldBeForecasted: response.shouldBeForecasted ?? null,
    type: response.type ?? null,
    priority: response.priority ?? null,
    sentDate: response.sentDate ?? null,
    deadlineDate: response.deadlineDate ?? null,
    answerReceivedDate: response.answerReceivedDate ?? null,
    revisionDate: response.revisionDate ?? null,
    answer: response.answer ?? null,
    costs: response.costs ?? [],
  };
}

function mapFormDataToUpCreateAtaBody(
  formData: AtaFormData,
): models.UpCreateAtaBody {
  return {
    projectId: formData.projectId ?? '',
    title: formData.title ?? '', // Ensure it is a string
    status: formData.status ?? ('' as models.domain.AtaStatusType),
    description: formData.description ?? null,
    shouldBeRegulated: formData.shouldBeRegulated ?? null,
    leadsToTimeExtension: formData.leadsToTimeExtension ?? null,
    pricingModel: formData.pricingModel ?? null,
    shouldBeForecasted: formData.shouldBeForecasted ?? null,
    type: formData.type ?? null,
    priority: formData.priority ?? null,
    sentDate: formData.sentDate ?? null,
    deadlineDate: formData.deadlineDate ?? null,
    answerReceivedDate: formData.answerReceivedDate ?? null,
    revisionDate: formData.revisionDate ?? null,
    answer: formData.answer ?? null,
    costs: formData.costs ?? [],
  } as models.UpCreateAtaBody;
}

const formatTimestamp = (timestamp: Date): string => {
  if (!timestamp) return '';
  return timestamp.toLocaleString('sv-SE', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
};

function buildEmptyFormData(): AtaFormData {
  return {
    projectId,
    id: null,
    title: null,
    status: models.domain.AtaStatusType.UnderDevelopment,
    serialNumber: null,
    description: null,
    shouldBeRegulated: null,
    leadsToTimeExtension: null,
    pricingModel: null,
    shouldBeForecasted: null,
    type: null,
    priority: 4,
    sentDate: null,
    deadlineDate: null,
    answerReceivedDate: null,
    revisionDate: null,
    answer: null,
    costs: [],
  };
}

function mapAtaChangelogsServerToClient(
  response: Awaited<ReturnType<typeof ataChangelogsService.getAllFromId>>,
): AtaChangelog[] {
  return response
    .slice()
    .sort((a, b) => b.timestamp.getTime() - a.timestamp.getTime())
    .map((changelog) => ({
      id: changelog.id,
      user: changelog.user.name,
      timestamp: formatTimestamp(changelog.timestamp),
      change: tChangelog(changelog.action, changelog.changes),
    }));
}

const detailQuery = useAtaDetailsQuery(
  () => projectId,
  () => ataId.value,
);
const changelogsQuery = useAtaChangelogsQuery(
  () => projectId,
  () => ataId.value,
);
const debitNormsQuery = useAtaDebitNormsQuery(() => projectId);
const documentQuery = useAtaDocumentQuery(
  () => projectId,
  () => ataId.value,
);
const projectDetailsQuery = useAtaProjectDetailsQuery(() => projectId);
const mutation = useAtaDetailsMutation(
  () => projectId,
  () => ataId.value,
);
const queryClient = useQueryClient();

const initialAta = computed<AtaFormData | null>(() => {
  if (isCreate.value) return buildEmptyFormData();
  return detailQuery.data.value
    ? mapGetAtaResponseToFormData(detailQuery.data.value)
    : null;
});

const changelogs = computed<AtaChangelog[]>(() =>
  mapAtaChangelogsServerToClient(changelogsQuery.data.value ?? []),
);

const initialDocument = computed<Blob | null>(
  () => documentQuery.data.value ?? null,
);

const isDocumentLoading = computed(() => documentQuery.isLoading.value);

const projectFortnoxId = computed<string | null>(
  () => projectDetailsQuery.data.value?.fortnoxId ?? null,
);
const projectName = computed<string | null>(
  () => projectDetailsQuery.data.value?.name ?? null,
);

const debitNormRates = computed<Record<number, number>>(() =>
  Object.fromEntries(
    (debitNormsQuery.data.value ?? []).map((norm) => [
      norm.account.number,
      norm.rate,
    ]),
  ),
);

const isFirstLoad = computed(
  () => !isCreate.value && detailQuery.isLoading.value,
);
const isUploadingDocument = ref(false);
const isUploadingFile = ref(false);
const isSaving = computed(
  () => mutation.isPending.value || isUploadingDocument.value,
);

async function onSave(
  formData: AtaFormData,
  documentPayload: AtaDocumentPayload,
  callbacks: AtaSaveCallbacks,
): Promise<void> {
  isUploadingDocument.value = true;
  if (documentPayload.dirty && documentPayload.file) {
    isUploadingFile.value = true;
  }
  try {
    const savedAta = await mutation.mutateAsync(
      mapFormDataToUpCreateAtaBody(formData),
    );

    try {
      if (documentPayload.dirty && documentPayload.file) {
        await ataService.uploadDocument(
          projectId,
          savedAta.id,
          documentPayload.file,
        );
        queryClient.setQueryData(
          projectKeys.ataDocument({
            projectId,
            ataId: savedAta.id,
          }),
          documentPayload.file,
        );
      } else if (documentPayload.dirty && documentPayload.file === null) {
        await ataService.deleteDocument(projectId, savedAta.id);
        queryClient.setQueryData(
          projectKeys.ataDocument({
            projectId,
            ataId: savedAta.id,
          }),
          null,
        );
      }
    } catch {
      if (ataId.value === '0') {
        queryClient.setQueryData(
          projectKeys.ataDetail({ projectId, ataId: savedAta.id }),
          savedAta,
        );
        ataId.value = savedAta.id;
        await router.replace({
          name: RouteName.ATADetails,
          params: { id: projectId, ataId: savedAta.id },
        });
      }

      notificationService.open({
        text: t('notifications.ataSavedDocumentFailed'),
        type: 'danger',
      });
      callbacks.onError();
      return;
    }

    notificationService.open({
      text: t('notifications.createSucess'),
      type: 'success',
    });

    callbacks.onSuccess();
  } catch {
    callbacks.onError();
  } finally {
    isUploadingDocument.value = false;
    isUploadingFile.value = false;
  }
}
</script>

<template>
  <AtaDetailsSkeleton v-if="isFirstLoad" />
  <AtaDetails
    v-else-if="initialAta"
    :initial-ata="initialAta"
    :initial-create-new-ata="isCreate"
    :changelogs="changelogs"
    :debit-norm-rates="debitNormRates"
    :ata-id="ataId"
    :project-id="projectId"
    :project-fortnox-id="projectFortnoxId"
    :project-name="projectName"
    :user="currentUser"
    :is-saving="isSaving"
    :initial-document="initialDocument"
    :is-document-loading="isDocumentLoading"
    @save="onSave"
  />
  <DocumentUploadOverlay
    :visible="isUploadingFile"
    :message="t('ata.details.uploadingDocument')"
  />
</template>
